import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../App';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Clock, Play, Pause, Volume2, VolumeX, 
  ChevronLeft, ChevronRight, AlertTriangle,
  SkipBack, SkipForward, Gauge, Send, FileText
} from 'lucide-react';
import { AudioPlayer } from '../components/AudioPlayer';

const ExerciseInterface = () => {
  const { exerciseId } = useParams();
  const navigate = useNavigate();
  
  const [exercise, setExercise] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // For listening/reading
  const [currentPartIndex, setCurrentPartIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  
  // For writing
  const [writingText, setWritingText] = useState('');
  
  // For speaking
  const [speakingPhase, setSpeakingPhase] = useState('prep'); // prep, recording, done
  const [prepTimeLeft, setPrepTimeLeft] = useState(0);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioURL, setAudioURL] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const prepTimerRef = useRef(null);
  const recordTimerRef = useRef(null);
  
  // Audio state
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [playCount, setPlayCount] = useState({});
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    fetchExercise();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [exerciseId]);

  const fetchExercise = async () => {
    try {
      const response = await api.get(`/exercises/${exerciseId}`);
      setExercise(response.data);
      setTimeLeft(response.data.time_limit_minutes * 60);
      
      // Initialize play counts for listening
      if (response.data.content?.parts) {
        const counts = {};
        response.data.content.parts.forEach((part) => {
          counts[part.part_id] = 0;
        });
        setPlayCount(counts);
      }
    } catch (error) {
      console.error('Error fetching exercise:', error);
      toast.error(error.response?.data?.detail || 'Failed to load exercise');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const startExercise = async () => {
    try {
      const response = await api.post(`/exercises/${exerciseId}/start`);
      setAttemptId(response.data.attempt_id);
      setStarted(true);
      
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      toast.success('Exercise started!');
    } catch (error) {
      console.error('Error starting exercise:', error);
      toast.error('Failed to start exercise');
    }
  };

  const handleAnswer = (questionId, answer) => {
    setAnswers((prev) => ({ ...prev, [questionId]: answer }));
  };

  const handleSubmit = async () => {
    if (submitting) return;
    
    setSubmitting(true);
    clearInterval(timerRef.current);
    
    try {
      let formattedAnswers = [];
      
      if (exercise.module === 'comprehension_orale' || exercise.module === 'comprehension_ecrite') {
        formattedAnswers = Object.entries(answers).map(([questionId, answer]) => ({
          question_id: questionId,
          answer: answer
        }));
      } else if (exercise.module === 'production_ecrite') {
        formattedAnswers = [{ text: writingText }];
      } else if (exercise.module === 'production_orale') {
        // Speaking: upload audio to dedicated endpoint
        if (!audioBlob) {
          toast.error('Please record your response first');
          setSubmitting(false);
          return;
        }
        const formData = new FormData();
        formData.append('audio', audioBlob, 'recording.webm');
        
        const speakingResponse = await api.post(
          `/speaking/submit/${exerciseId}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        
        navigate(`/results/${attemptId || 'speaking'}`, { 
          state: { 
            results: {
              score: speakingResponse.data.score,
              total_points: speakingResponse.data.total_points,
              passed: speakingResponse.data.passed,
              corrections: [{
                type: 'speaking_evaluation',
                transcript: speakingResponse.data.transcript,
                evaluation: speakingResponse.data.evaluation,
                criteria: speakingResponse.data.criteria
              }]
            },
            exercise: exercise
          } 
        });
        return;
      }
      
      const response = await api.post(`/exercises/${exerciseId}/submit`, {
        attempt_id: attemptId,
        answers: formattedAnswers
      });
      
      navigate(`/results/${attemptId}`, { 
        state: { 
          results: response.data,
          exercise: exercise
        } 
      });
    } catch (error) {
      console.error('Error submitting:', error);
      toast.error('Failed to submit');
      setSubmitting(false);
    }
  };

  // Audio controls for listening exercises
  const currentPart = exercise?.content?.parts?.[currentPartIndex];
  const canPlay = currentPart && playCount[currentPart.part_id] < currentPart.play_limit;

  const togglePlay = useCallback(() => {
    if (!audioRef.current || !currentPart) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      if (!canPlay) {
        toast.warning(`You can only play this audio ${currentPart.play_limit} time(s)`);
        return;
      }
      audioRef.current.play();
    }
  }, [isPlaying, canPlay, currentPart]);

  const handleAudioPlay = () => setIsPlaying(true);
  const handleAudioPause = () => setIsPlaying(false);
  
  const handleAudioEnded = () => {
    setIsPlaying(false);
    setPlayCount((prev) => ({
      ...prev,
      [currentPart.part_id]: (prev[currentPart.part_id] || 0) + 1
    }));
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) setAudioProgress(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) setAudioDuration(audioRef.current.duration);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimerClass = () => {
    if (timeLeft <= 60) return 'text-red-500 animate-pulse';
    if (timeLeft <= 300) return 'text-orange-500';
    return '';
  };

  // Get all questions for navigation
  const getAllQuestions = () => {
    if (exercise?.module === 'comprehension_orale') {
      return exercise.content.parts.flatMap((part, partIndex) => 
        part.questions.map((q, qIndex) => ({ ...q, partIndex, questionIndex: qIndex }))
      );
    } else if (exercise?.module === 'comprehension_ecrite') {
      return exercise.content.texts.flatMap((text, textIndex) =>
        text.questions.map((q, qIndex) => ({ ...q, textIndex, questionIndex: qIndex }))
      );
    }
    return [];
  };

  const allQuestions = getAllQuestions();
  const flatIndex = allQuestions.findIndex(
    q => (q.partIndex !== undefined ? q.partIndex : q.textIndex) === currentPartIndex && q.questionIndex === currentQuestionIndex
  );

  const goToNext = () => {
    const parts = exercise?.content?.parts || exercise?.content?.texts || [];
    const currentQuestions = parts[currentPartIndex]?.questions || [];
    
    if (currentQuestionIndex < currentQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else if (currentPartIndex < parts.length - 1) {
      setCurrentPartIndex(prev => prev + 1);
      setCurrentQuestionIndex(0);
      setAudioProgress(0);
    }
  };

  const goToPrev = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    } else if (currentPartIndex > 0) {
      const parts = exercise?.content?.parts || exercise?.content?.texts || [];
      setCurrentPartIndex(prev => prev - 1);
      setCurrentQuestionIndex(parts[currentPartIndex - 1].questions.length - 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  // Start Screen
  if (!started) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-3xl p-8 max-w-lg w-full shadow-xl border border-border/50"
        >
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
              {exercise.title_fr}
            </h1>
            <p className="text-muted-foreground">{exercise.description_fr}</p>
          </div>
          
          <div className="space-y-4 mb-8">
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
              <span className="text-muted-foreground">Duration</span>
              <span className="font-semibold">{exercise.time_limit_minutes} minutes</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
              <span className="text-muted-foreground">Total Points</span>
              <span className="font-semibold">{exercise.total_points} points</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
              <span className="text-muted-foreground">Pass Score</span>
              <span className="font-semibold text-primary">
                {exercise.pass_score ? `${exercise.pass_score}/${exercise.total_points}` : '80%'}
              </span>
            </div>
          </div>
          
          {exercise.module === 'comprehension_orale' && (
            <div className="bg-accent/10 rounded-xl p-4 mb-8">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-accent mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-accent mb-1">Audio Rules:</p>
                  <ul className="text-muted-foreground space-y-1">
                    <li>• Part 1: Audio plays <strong>once only</strong></li>
                    <li>• Part 2: Audio plays <strong>twice maximum</strong></li>
                  </ul>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex gap-4">
            <Button variant="outline" className="flex-1 rounded-full" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button className="flex-1 rounded-full font-bold" onClick={startExercise} data-testid="start-exercise-btn">
              Start Exercise
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Writing Exercise
  if (exercise.module === 'production_ecrite') {
    const wordCount = writingText.trim().split(/\s+/).filter(w => w).length;
    const minWords = exercise.content?.word_count_min || 150;
    const maxWords = exercise.content?.word_count_max || 250;
    const isUnderMin = wordCount < minWords;
    const isOverMax = wordCount > maxWords;
    const isInRange = !isUnderMin && !isOverMax;

    return (
      <div className="min-h-screen bg-[#FDFCF8] flex flex-col">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-white border-b border-border shadow-sm">
          <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="exit-writing-btn">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Exit
            </Button>
            
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full bg-muted font-mono text-lg ${getTimerClass()}`}>
              <Clock className="w-5 h-5" />
              <span>{formatTime(timeLeft)}</span>
            </div>
            
            <Button 
              onClick={handleSubmit} 
              disabled={submitting || wordCount === 0} 
              className="rounded-full font-semibold gap-2" 
              data-testid="submit-writing-btn"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Correcting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Corriger avec l'IA
                </>
              )}
            </Button>
          </div>
        </header>

        <main className="flex-1 max-w-4xl mx-auto w-full px-4 py-6 space-y-6">
          {/* Task Prompt */}
          <div className="bg-white rounded-2xl p-6 border border-border/50 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <FileText className="w-4 h-4 text-primary" />
              </div>
              <h2 className="font-bold text-lg" style={{ fontFamily: 'Syne, sans-serif' }}>
                {exercise.content?.format_fr || 'Production Ecrite'}
              </h2>
            </div>
            <div className="text-[15px] leading-relaxed whitespace-pre-line text-foreground">
              {exercise.content?.prompt_fr || exercise.description_fr}
            </div>
            
            {/* Constraints */}
            {exercise.content?.constraints_fr && (
              <div className="mt-4 p-3 bg-amber-50 rounded-xl border border-amber-200">
                <p className="text-sm font-medium text-amber-800 mb-1">Contraintes :</p>
                <ul className="text-sm text-amber-700 space-y-0.5">
                  {exercise.content.constraints_fr.map((c, i) => (
                    <li key={i}>- {c}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Tips */}
            {exercise.content?.tips_fr && (
              <div className="mt-3 p-3 bg-blue-50 rounded-xl border border-blue-200">
                <p className="text-sm font-medium text-blue-800 mb-1">Conseils :</p>
                <ul className="text-sm text-blue-700 space-y-0.5">
                  {exercise.content.tips_fr.map((t, i) => (
                    <li key={i}>- {t}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Grading Criteria */}
          {exercise.content?.criteria && (
            <div className="bg-white rounded-2xl p-6 border border-border/50 shadow-sm">
              <h3 className="font-bold text-sm uppercase tracking-wider text-muted-foreground mb-3">
                Grille d'evaluation (/25)
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {exercise.content.criteria.map((c, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 bg-muted/50 rounded-lg">
                    <span className="text-sm">{c.name_fr}</span>
                    <span className="text-sm font-bold text-primary">/{c.max_points}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Writing Area */}
          <div className="bg-white rounded-2xl p-6 border border-border/50 shadow-sm">
            <Textarea
              value={writingText}
              onChange={(e) => setWritingText(e.target.value)}
              placeholder="Redigez votre texte ici..."
              className="min-h-[350px] text-base leading-relaxed resize-y border-0 focus-visible:ring-0 p-0"
              data-testid="writing-textarea"
            />
            
            {/* Word Counter Bar */}
            <div className="mt-4 pt-4 border-t border-border/50">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-medium ${
                    isInRange ? 'text-green-600' : isOverMax ? 'text-red-600' : 'text-amber-600'
                  }`}>
                    {wordCount} mot{wordCount !== 1 ? 's' : ''}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    (min {minWords} / max {maxWords})
                  </span>
                </div>
                {isInRange && (
                  <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                    Nombre de mots OK
                  </span>
                )}
                {isUnderMin && wordCount > 0 && (
                  <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                    {minWords - wordCount} mots restants
                  </span>
                )}
                {isOverMax && (
                  <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                    {wordCount - maxWords} mots en trop
                  </span>
                )}
              </div>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all duration-300 ${
                    isInRange ? 'bg-green-500' : isOverMax ? 'bg-red-500' : 'bg-amber-500'
                  }`}
                  style={{ width: `${Math.min((wordCount / maxWords) * 100, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Speaking Exercise
  if (exercise.module === 'production_orale') {
    const prepTime = exercise.content?.prep_time_seconds || 120;
    const maxSpeaking = exercise.content?.speaking_time_max_seconds || 180;

    const startPrep = () => {
      setSpeakingPhase('prep');
      setPrepTimeLeft(prepTime);
      prepTimerRef.current = setInterval(() => {
        setPrepTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(prepTimerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    };

    const startRecording = async () => {
      try {
        clearInterval(prepTimerRef.current);
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
        mediaRecorderRef.current = mediaRecorder;
        audioChunksRef.current = [];

        mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) audioChunksRef.current.push(e.data);
        };

        mediaRecorder.onstop = () => {
          const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          setAudioBlob(blob);
          setAudioURL(URL.createObjectURL(blob));
          stream.getTracks().forEach(t => t.stop());
          setSpeakingPhase('done');
        };

        mediaRecorder.start();
        setIsRecording(true);
        setSpeakingPhase('recording');
        setRecordingTime(0);

        recordTimerRef.current = setInterval(() => {
          setRecordingTime(prev => {
            if (prev >= maxSpeaking) {
              mediaRecorder.stop();
              setIsRecording(false);
              clearInterval(recordTimerRef.current);
              return prev;
            }
            return prev + 1;
          });
        }, 1000);
      } catch (err) {
        toast.error('Microphone access required. Please allow microphone access.');
      }
    };

    const stopRecording = () => {
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
        clearInterval(recordTimerRef.current);
      }
    };

    const resetRecording = () => {
      setAudioBlob(null);
      setAudioURL(null);
      setSpeakingPhase('prep');
      setRecordingTime(0);
      setPrepTimeLeft(prepTime);
    };

    const fmtSec = (s) => `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`;

    return (
      <div className="min-h-screen bg-[#FDFCF8] flex flex-col">
        <header className="sticky top-0 z-50 bg-white border-b border-border shadow-sm">
          <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="exit-speaking-btn">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Exit
            </Button>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted font-mono text-lg">
              <Clock className="w-5 h-5" />
              <span>{formatTime(timeLeft)}</span>
            </div>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !audioBlob}
              className="rounded-full font-semibold gap-2"
              data-testid="submit-speaking-btn"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Corriger avec l'IA
                </>
              )}
            </Button>
          </div>
        </header>

        <main className="flex-1 max-w-4xl mx-auto w-full px-4 py-6 space-y-6">
          {/* Task Prompt */}
          <div className="bg-white rounded-2xl p-6 border border-border/50 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Volume2 className="w-4 h-4 text-primary" />
              </div>
              <h2 className="font-bold text-lg" style={{ fontFamily: 'Syne, sans-serif' }}>
                {exercise.content?.format_fr || 'Production Orale'}
              </h2>
            </div>
            <p className="text-[17px] font-medium leading-relaxed mb-4 text-foreground">
              {exercise.content?.prompt_fr}
            </p>

            {exercise.content?.instructions_fr && (
              <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
                <p className="text-sm font-medium text-amber-800 mb-1">Dans votre reponse :</p>
                <ul className="text-sm text-amber-700 space-y-0.5">
                  {exercise.content.instructions_fr.map((inst, i) => (
                    <li key={i}>- {inst}</li>
                  ))}
                </ul>
              </div>
            )}

            {exercise.content?.tips_fr && (
              <div className="mt-3 p-3 bg-blue-50 rounded-xl border border-blue-200">
                <p className="text-sm font-medium text-blue-800 mb-1">Conseils :</p>
                <ul className="text-sm text-blue-700 space-y-0.5">
                  {exercise.content.tips_fr.map((t, i) => (
                    <li key={i}>- {t}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Grading Criteria */}
          {exercise.content?.criteria && (
            <div className="bg-white rounded-2xl p-6 border border-border/50 shadow-sm">
              <h3 className="font-bold text-sm uppercase tracking-wider text-muted-foreground mb-3">
                Grille d'evaluation (/25)
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {exercise.content.criteria.map((c, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 bg-muted/50 rounded-lg">
                    <span className="text-sm">{c.name_fr}</span>
                    <span className="text-sm font-bold text-primary">/{c.max_points}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recording Interface */}
          <div className="bg-white rounded-2xl p-8 border border-border/50 shadow-sm">
            {/* Prep Phase */}
            {speakingPhase === 'prep' && !isRecording && !audioBlob && (
              <div className="text-center space-y-6">
                <div className="w-20 h-20 rounded-full bg-blue-50 border-2 border-blue-200 flex items-center justify-center mx-auto">
                  <FileText className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-xl mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>
                    Preparation
                  </h3>
                  <p className="text-muted-foreground">
                    {prepTimeLeft > 0
                      ? `Temps de preparation : ${fmtSec(prepTimeLeft)}`
                      : `Vous avez ${fmtSec(prepTime)} pour preparer votre reponse`
                    }
                  </p>
                </div>
                {prepTimeLeft === 0 ? (
                  <div className="flex gap-3 justify-center">
                    <Button onClick={startPrep} variant="outline" className="rounded-full px-6" data-testid="start-prep-btn">
                      Start Prep Timer
                    </Button>
                    <Button onClick={startRecording} className="rounded-full px-6 gap-2 bg-red-600 hover:bg-red-700" data-testid="start-record-btn">
                      <div className="w-3 h-3 rounded-full bg-white" />
                      Skip to Recording
                    </Button>
                  </div>
                ) : (
                  <Button onClick={startRecording} className="rounded-full px-8 gap-2 bg-red-600 hover:bg-red-700" data-testid="start-record-from-prep-btn">
                    <div className="w-3 h-3 rounded-full bg-white" />
                    Start Recording
                  </Button>
                )}
              </div>
            )}

            {/* Recording Phase */}
            {speakingPhase === 'recording' && isRecording && (
              <div className="text-center space-y-6">
                <div className="w-20 h-20 rounded-full bg-red-50 border-2 border-red-300 flex items-center justify-center mx-auto animate-pulse">
                  <div className="w-6 h-6 rounded-full bg-red-500" />
                </div>
                <div>
                  <h3 className="font-bold text-xl text-red-600 mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>
                    Recording...
                  </h3>
                  <p className="text-2xl font-mono font-bold">{fmtSec(recordingTime)}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Max {fmtSec(maxSpeaking)}
                  </p>
                </div>
                <div className="w-full h-2 bg-muted rounded-full overflow-hidden max-w-xs mx-auto">
                  <div
                    className="h-full rounded-full bg-red-500 transition-all duration-1000"
                    style={{ width: `${(recordingTime / maxSpeaking) * 100}%` }}
                  />
                </div>
                <Button onClick={stopRecording} variant="destructive" className="rounded-full px-8 gap-2" data-testid="stop-record-btn">
                  <Pause className="w-4 h-4" />
                  Stop Recording
                </Button>
              </div>
            )}

            {/* Done Phase - Playback */}
            {speakingPhase === 'done' && audioURL && (
              <div className="text-center space-y-6">
                <div className="w-20 h-20 rounded-full bg-green-50 border-2 border-green-200 flex items-center justify-center mx-auto">
                  <Volume2 className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <h3 className="font-bold text-xl text-green-600 mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>
                    Recording Complete
                  </h3>
                  <p className="text-muted-foreground">Duration: {fmtSec(recordingTime)}</p>
                </div>
                <audio src={audioURL} controls className="mx-auto" data-testid="playback-audio" />
                <div className="flex gap-3 justify-center">
                  <Button onClick={resetRecording} variant="outline" className="rounded-full px-6" data-testid="re-record-btn">
                    Re-record
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="rounded-full px-6 gap-2"
                    data-testid="submit-recording-btn"
                  >
                    {submitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Corriger avec l'IA
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    );
  }

  // Listening / Reading Exercise
  const parts = exercise?.content?.parts || exercise?.content?.texts || [];
  const currentPartData = parts[currentPartIndex];
  const currentQuestion = currentPartData?.questions?.[currentQuestionIndex];

  return (
    <div className="min-h-screen bg-[#FDFCF8] flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-border shadow-sm">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => {
            if (window.confirm('Are you sure you want to leave?')) {
              clearInterval(timerRef.current);
              navigate(-1);
            }
          }}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            Exit
          </Button>
          
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full bg-muted font-mono text-lg ${getTimerClass()}`}>
            <Clock className="w-5 h-5" />
            <span data-testid="timer">{formatTime(timeLeft)}</span>
          </div>
          
          <Button onClick={handleSubmit} disabled={submitting} className="rounded-full font-semibold" data-testid="submit-btn">
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </div>
        <Progress value={(flatIndex + 1) / allQuestions.length * 100} className="h-1 rounded-none" />
      </header>

      <main className="flex-1 max-w-4xl mx-auto w-full px-4 py-6">
        {/* Part Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
              {currentPartData?.title_fr || `Part ${currentPartIndex + 1}`}
            </h2>
            <span className="text-sm px-3 py-1 rounded-full bg-muted">
              Part {currentPartIndex + 1}/{parts.length}
            </span>
          </div>
        </div>

        {/* Audio Player for Listening (DB data with audio_url) */}
        {exercise.module === 'comprehension_orale' && currentPartData?.audio_url && (
          <div className="bg-white rounded-2xl p-6 mb-6 border border-border/50 shadow-sm">
            <audio
              ref={audioRef}
              src={currentPartData.audio_url}
              onPlay={handleAudioPlay}
              onPause={handleAudioPause}
              onEnded={handleAudioEnded}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
            />
            
            <div className="text-sm text-muted-foreground mb-3">
              Plays remaining: {Math.max(0, currentPartData.play_limit - (playCount[currentPartData.part_id] || 0))}/{currentPartData.play_limit}
            </div>
            
            <div className="audio-progress mb-4" onClick={(e) => {
              if (!canPlay && !isPlaying) return;
              const rect = e.currentTarget.getBoundingClientRect();
              const percent = (e.clientX - rect.left) / rect.width;
              if (audioRef.current) audioRef.current.currentTime = percent * audioDuration;
            }}>
              <div className="audio-progress-fill" style={{ width: `${audioDuration ? (audioProgress / audioDuration) * 100 : 0}%` }} />
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm font-mono text-muted-foreground">{formatTime(audioProgress)}</span>
              <div className="flex items-center gap-2">
                <Button size="lg" className={`rounded-full w-14 h-14 ${!canPlay && !isPlaying ? 'opacity-50' : ''}`}
                  onClick={togglePlay} disabled={!canPlay && !isPlaying} data-testid="audio-play-btn">
                  {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
                </Button>
              </div>
              <span className="text-sm font-mono text-muted-foreground">{formatTime(audioDuration)}</span>
            </div>
          </div>
        )}

        {/* Audio Player for Listening (curriculum-based, server audio files) */}
        {exercise.module === 'comprehension_orale' && !currentPartData?.audio_url && (
          <div className="mb-6">
            <AudioPlayer 
              level={exercise.level}
              module={exercise.module}
              exerciseNumber={exercise.exercise_number}
              label={currentPartData?.title_fr || `Audio - Part ${currentPartIndex + 1}`}
            />
          </div>
        )}

        {/* Reading Text */}
        {exercise.module === 'comprehension_ecrite' && currentPartData?.content && (
          <div className="bg-white rounded-2xl p-6 mb-6 border border-border/50 max-h-96 overflow-y-auto">
            <p className="text-base leading-relaxed whitespace-pre-line">{currentPartData.content_fr || currentPartData.content}</p>
          </div>
        )}

        {/* Question Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion?.question_id}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="bg-white rounded-2xl p-6 border border-border/50 shadow-sm"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-muted-foreground">
                Question {currentQuestionIndex + 1}/{currentPartData?.questions?.length}
              </span>
              <span className="text-sm font-medium px-2 py-1 rounded-full bg-primary/10 text-primary">
                {currentQuestion?.points || 2} pts
              </span>
            </div>
            
            <h3 className="text-lg font-semibold mb-2">{currentQuestion?.text}</h3>
            <p className="text-muted-foreground mb-6">{currentQuestion?.text_fr}</p>
            
            <RadioGroup
              value={answers[currentQuestion?.question_id] || ''}
              onValueChange={(value) => handleAnswer(currentQuestion?.question_id, value)}
              className="space-y-3"
            >
              {currentQuestion?.options?.map((option, index) => (
                <div
                  key={index}
                  className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    answers[currentQuestion?.question_id] === option ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => handleAnswer(currentQuestion?.question_id, option)}
                  data-testid={`option-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <RadioGroupItem value={option} id={`option-${index}`} />
                    <Label htmlFor={`option-${index}`} className="flex-1 cursor-pointer">{option}</Label>
                  </div>
                </div>
              ))}
            </RadioGroup>
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <Button variant="outline" onClick={goToPrev} disabled={currentPartIndex === 0 && currentQuestionIndex === 0} className="rounded-full">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Previous
          </Button>
          
          <div className="flex gap-1.5">
            {allQuestions.map((q, index) => (
              <button
                key={index}
                className={`w-2.5 h-2.5 rounded-full transition-colors ${
                  index === flatIndex ? 'bg-primary' : answers[q.question_id] ? 'bg-green-500' : 'bg-muted'
                }`}
                onClick={() => {
                  setCurrentPartIndex(q.partIndex || q.textIndex || 0);
                  setCurrentQuestionIndex(q.questionIndex);
                }}
              />
            ))}
          </div>
          
          <Button onClick={goToNext} 
            disabled={currentPartIndex === parts.length - 1 && currentQuestionIndex === currentPartData?.questions?.length - 1} 
            className="rounded-full">
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </main>
    </div>
  );
};

export default ExerciseInterface;
